/**
 * ゲームデータの読み込み口。
 *
 * 【いま読んでいるデータ】
 * 実データ（src/data/）。ダミーデータ（src/data/__fixtures__/）に戻したい場合は
 * 下の import のパスを差し替える。ただし両者には次の違いがあるので注意する。
 *   - 実データは interactables を world.json の中に持つ（ダミーは interactables.json）
 *   - 実データの glossary.json は { term, reading, short, analogy, firstChapter } 形式で、
 *     GlossaryEntry（term / aliases / short / analogy / cardId）とは食い違う。
 *     このファイルの toGlossaryEntries() で吸収する。
 *
 * 【データの前提】
 * - world.json は NetworkWorld（devices / links / rooms）に加えて、
 *   任意で `layout`（3D の部屋寸法とスポーン地点）を持てる。省略時は自動生成する。
 * - interactables の `position` は「部屋の中心を原点とするローカル座標」（y は床からの高さ）。
 *   部屋の配置はレイアウト側が決めるため、世界座標では書けない。
 *   絶対座標で書かれたデータは tools/convert-coords.mjs で変換する。
 * - chapters/*.json は Chapter 型そのまま。dialogues.json は Dialogue の配列。
 */

import type {
  Chapter,
  Dialogue,
  Interactable,
  KnowledgeCard,
} from '../types/scenario';
import type { WorldData } from '../engine/layout';
import { normalizeWorld, validateWorld } from '../sim/world';

// ---- データの差し替えはこの import のパスだけを変更する -------------------
import worldJson from '../data/world.json';
import dialoguesJson from '../data/dialogues.json';
import knowledgeCardsJson from '../data/knowledge-cards.json';
import glossaryJson from '../data/glossary.json';
import chapter00Json from '../data/chapters/ch00.json';
import chapter01Json from '../data/chapters/ch01.json';
import chapter02Json from '../data/chapters/ch02.json';
import chapter03Json from '../data/chapters/ch03.json';
import chapter04Json from '../data/chapters/ch04.json';
import chapter05Json from '../data/chapters/ch05.json';
import chapter06Json from '../data/chapters/ch06.json';
// -------------------------------------------------------------------------

/** 用語集の 1 項目。会話中のハイライトとポップアップに使う */
export interface GlossaryEntry {
  term: string;
  /** 本文中で同じ説明にリンクさせたい別表記（「IPアドレス」「IP アドレス」など） */
  aliases?: string[];
  /** ひとことでの説明 */
  short: string;
  /** たとえ話 */
  analogy?: string;
  /** 対応する知識カード */
  cardId?: string;
}

export interface GameData {
  world: WorldData;
  chapters: Chapter[];
  dialogues: Dialogue[];
  interactables: Interactable[];
  knowledgeCards: KnowledgeCard[];
  glossary: GlossaryEntry[];
}

/** 実データの glossary.json の 1 行（GlossaryEntry とは形が違う） */
interface RawGlossaryEntry {
  term: string;
  /** 読みがな。表示には使わないが、データ側の索引用に入っている */
  reading?: string;
  short: string;
  analogy?: string;
  /** その用語が初めて出てくる章 */
  firstChapter?: string;
}

/**
 * 用語集を GlossaryEntry へ変換する。
 *
 * 実データの用語集は cardId を持たないため、知識カードとの対応をここで割り出す。
 *   1. カードの term が用語と完全一致するもの
 *   2. カードの term が「用語＋区切り文字」で始まるもの
 *      （例：「パッチパネル」→「パッチパネルと配線盤」、「切り分け」→「切り分け ―― まず物理から疑え」）
 * どちらにも当たらなければ cardId は付けない（用語ポップアップにカードへのリンクが出ないだけ）。
 */
function toGlossaryEntries(
  raw: RawGlossaryEntry[],
  cards: KnowledgeCard[],
): GlossaryEntry[] {
  const SEPARATORS = [' ', '　', 'と', '、', '―'];
  return raw.map((g) => {
    const exact = cards.find((c) => c.term === g.term);
    const prefixed =
      exact ??
      cards.find(
        (c) => c.term.startsWith(g.term) && SEPARATORS.includes(c.term.charAt(g.term.length)),
      );
    const entry: GlossaryEntry = { term: g.term, short: g.short };
    if (g.analogy) entry.analogy = g.analogy;
    if (prefixed) entry.cardId = prefixed.id;
    return entry;
  });
}

/**
 * JSON は構造的に推論されるため、契約型へは一度だけ変換する。
 * ここで validateWorld を通すことで、データの取り違えを起動時に検出する。
 */
export function loadGameData(): GameData {
  // 実データは interactables を world.json の中に持つ（NetworkWorld 型には無いキー）
  const world = worldJson as unknown as WorldData & { interactables?: Interactable[] };
  normalizeWorld(world);
  const errors = validateWorld(world);
  if (errors.length > 0) {
    console.error('[data] world.json に問題があります:\n' + errors.join('\n'));
  }

  const interactables = world.interactables ?? [];
  const knowledgeCards = knowledgeCardsJson as unknown as KnowledgeCard[];

  return {
    world,
    chapters: [
      chapter00Json,
      chapter01Json,
      chapter02Json,
      chapter03Json,
      chapter04Json,
      chapter05Json,
      chapter06Json,
    ] as unknown as Chapter[],
    dialogues: dialoguesJson as unknown as Dialogue[],
    interactables,
    knowledgeCards,
    glossary: toGlossaryEntries(glossaryJson as unknown as RawGlossaryEntry[], knowledgeCards),
  };
}

/** id 引きしやすい形に整える */
export function indexById<T extends { id: string }>(items: T[]): Record<string, T> {
  const map: Record<string, T> = {};
  for (const item of items) map[item.id] = item;
  return map;
}
